(() => {
  if (window.AlawooderFounderStory) {
    window.AlawooderFounderStory.mountAll();
    return;
  }

  const instances = new Map();
  const clamp = (value, min = 0, max = 1) => Math.max(min, Math.min(max, value));
  const ease = value => value * value * (3 - 2 * value);
  const number = (value, fallback = 0) => Number.isFinite(Number(value)) ? Number(value) : fallback;
  const svgNamespace = 'http://www.w3.org/2000/svg';
  let instanceSequence = 0;

  function mount(root) {
    if (instances.has(root)) return;

    const rail = root.querySelector('[data-fs-rail]');
    const sticky = root.querySelector('[data-fs-sticky]');
    const art = root.querySelector('[data-fs-art]');
    const world = root.querySelector('[data-fs-world]');
    const actor = root.querySelector('[data-fs-actor]');
    const chapters = Array.from(root.querySelectorAll('[data-fs-chapter]'));
    const navigation = Array.from(root.querySelectorAll('[data-fs-go]'));
    const counts = Array.from(root.querySelectorAll('[data-fs-count]'));
    const progressBars = Array.from(root.querySelectorAll('[data-fs-progress]'));
    const modeButtons = Array.from(root.querySelectorAll('[data-fs-mode]'));
    const reading = root.querySelector('[data-fs-reading]');
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const mobile = window.matchMedia('(max-width: 760px)');
    const sceneCount = 6;
    const sceneWidth = 1100;
    const transforms = new Map();
    const cleanups = [];
    const originalModeButtons = modeButtons.map(button => ({ button, hidden: button.hidden, disabled: button.disabled }));
    const clipPrefix = `aw-founder-${++instanceSequence}`;
    let clipDefinitions = null;
    let geometryCache = null;
    let frame = 0;
    let nearby = true;
    let active = -1;
    let mode = '';
    let previousPanelMode = '';
    let destroyed = false;

    const originalViewBox = art?.getAttribute('viewBox') || '0 110 1200 480';
    const originalRootMode = root.getAttribute('data-mode');
    const chapterIndices = chapters.map(chapter => Number(chapter.getAttribute('data-fs-scene-index')));
    const completeChapters = chapters.length === sceneCount
      && chapters.every(chapter => chapter.hasAttribute('data-fs-scene-index'))
      && chapterIndices.every(index => Number.isInteger(index) && index >= 0 && index < sceneCount)
      && new Set(chapterIndices).size === sceneCount;
    const canUseMotion = Boolean(rail && sticky && art && world && completeChapters);
    const sceneOf = node => clamp(number(node.dataset.fsSceneIndex), 0, sceneCount - 1);
    const navigationIndex = node => clamp(number(node.getAttribute('data-fs-go') || node.value), 0, sceneCount - 1);

    const rememberTransform = node => {
      if (node && !transforms.has(node)) transforms.set(node, node.getAttribute('transform'));
      return transforms.get(node) || '';
    };
    const timingFor = node => {
      const delay = clamp(number(node.dataset.fsDelay));
      return { delay, end: clamp(number(node.dataset.fsEnd, Math.min(1, delay + 0.38))) };
    };

    const drawings = Array.from(root.querySelectorAll('[data-fs-draw]')).flatMap(node => {
      if (typeof node.getTotalLength !== 'function') return [];
      let length;
      try { length = node.getTotalLength(); } catch (_) { return []; }
      if (!Number.isFinite(length) || length <= 0) return [];
      const originalDash = node.style.strokeDasharray;
      const originalOffset = node.style.strokeDashoffset;
      node.style.strokeDasharray = String(length);
      cleanups.push(() => {
        node.style.strokeDasharray = originalDash;
        node.style.strokeDashoffset = originalOffset;
      });
      return [{ node, length, scene: number(node.dataset.fsDraw), ...timingFor(node) }];
    });
    const fills = Array.from(root.querySelectorAll('[data-fs-fill]')).map(node => {
      const opacity = node.style.fillOpacity;
      const baseOpacity = clamp(number(window.getComputedStyle(node).fillOpacity, 1));
      const timing = timingFor(node);
      const sharesOutline = node.hasAttribute('data-fs-draw');
      const delay = clamp(number(node.dataset.fsFillDelay, sharesOutline ? Math.max(0.38, timing.delay + 0.2) : timing.delay));
      const end = clamp(number(node.dataset.fsFillEnd, sharesOutline ? Math.max(delay + 0.25, timing.end + 0.12) : timing.end));
      cleanups.push(() => { node.style.fillOpacity = opacity; });
      return { node, baseOpacity, scene: number(node.dataset.fsFill), delay, end };
    });
    const growing = Array.from(root.querySelectorAll('[data-fs-grow]')).map(node => {
      const opacity = node.style.opacity;
      const baseOpacity = clamp(number(window.getComputedStyle(node).opacity, 1));
      const originalClip = node.getAttribute('clip-path');
      const hasOutline = node.hasAttribute('data-fs-draw') || Boolean(node.querySelector('[data-fs-draw]'));
      let reveal = null;
      let bounds = null;
      if (art && !hasOutline && !originalClip && !node.style.clipPath && typeof node.getBBox === 'function') {
        try {
          const box = node.getBBox();
          if (box.width > 0 && box.height > 0) {
            if (!clipDefinitions) {
              clipDefinitions = document.createElementNS(svgNamespace, 'defs');
              art.prepend(clipDefinitions);
              cleanups.push(() => clipDefinitions.remove());
            }
            const clip = document.createElementNS(svgNamespace, 'clipPath');
            clip.id = `${clipPrefix}-${clipDefinitions.children.length}`;
            clip.setAttribute('clipPathUnits', 'userSpaceOnUse');
            reveal = document.createElementNS(svgNamespace, 'rect');
            bounds = { x: box.x - 2, y: box.y - 2, width: box.width + 4, height: box.height + 4 };
            reveal.setAttribute('x', String(bounds.x));
            reveal.setAttribute('width', String(bounds.width));
            clip.append(reveal);
            clipDefinitions.append(clip);
            node.setAttribute('clip-path', `url(#${clip.id})`);
          }
        } catch (_) { /* A small optional detail can fall back to opacity. */ }
      }
      cleanups.push(() => {
        node.style.opacity = opacity;
        if (originalClip === null) node.removeAttribute('clip-path');
        else node.setAttribute('clip-path', originalClip);
      });
      return { node, baseOpacity, reveal, bounds, hasOutline, scene: number(node.dataset.fsGrow), ...timingFor(node) };
    });
    rememberTransform(world);
    rememberTransform(actor);

    function topInset() {
      const stickyTop = sticky && parseFloat(window.getComputedStyle(sticky).top);
      if (Number.isFinite(stickyTop)) return Math.max(0, stickyTop);
      return Math.max(0, parseFloat(window.getComputedStyle(document.documentElement).getPropertyValue('--ah-header-height')) || 0);
    }

    function geometry() {
      if (geometryCache) return geometryCache;
      const bounds = rail.getBoundingClientRect();
      geometryCache = {
        start: bounds.top + window.scrollY - topInset(),
        distance: Math.max(1, bounds.height - sticky.getBoundingClientRect().height)
      };
      return geometryCache;
    }

    function scrollToTop(top, smooth = false) {
      window.scrollTo({ left: window.scrollX, top: Math.max(0, top), behavior: smooth && !reduced.matches ? 'smooth' : 'instant' });
    }

    function updateChapter(index) {
      if (index === active && previousPanelMode === mode) return;
      active = index;
      previousPanelMode = mode;
      root.dataset.fsActive = String(index);
      chapters.forEach(chapter => {
        const current = sceneOf(chapter) === index;
        chapter.hidden = mode !== 'motion' || !current;
        chapter.classList.toggle('is-active', current);
      });
      navigation.forEach(button => {
        const current = navigationIndex(button) === index;
        if (current) button.setAttribute('aria-current', 'step');
        else button.removeAttribute('aria-current');
      });
      counts.forEach(count => {
        count.removeAttribute('aria-live');
        count.textContent = `${String(index + 1).padStart(2, '0')} / ${String(sceneCount).padStart(2, '0')}`;
      });
    }

    function draw(progress) {
      const scaled = progress * sceneCount;
      const index = Math.min(sceneCount - 1, Math.floor(scaled));
      const phase = progress === 1 ? 1 : scaled - index;
      const transition = index > 0 ? clamp(phase / 0.42) : 1;
      const camera = reduced.matches ? index * sceneWidth : (index > 0 ? index - 1 + ease(transition) : 0) * sceneWidth;
      const chapterGrowth = reduced.matches || mode === 'reading' ? 1 : index === 0 ? 0.1 + 0.9 * phase : phase;
      const growthFor = (scene, delay, end) => {
        if (scene < index) return 1;
        if (scene > index || chapterGrowth < delay) return 0;
        if (chapterGrowth >= end || chapterGrowth === 1) return 1;
        return ease(clamp((chapterGrowth - delay) / Math.max(0.001, end - delay)));
      };

      if (world) world.setAttribute('transform', `translate(${(-camera).toFixed(3)} 0)`);
      const settling = !reduced.matches && mode === 'motion' && index > 0 && transition < 1
        ? Math.sin(transition * Math.PI * 4) * Math.sin(transition * Math.PI) * 1.6 : 0;
      if (actor) actor.setAttribute('transform', `translate(${(camera + 180).toFixed(3)} ${(510 + settling).toFixed(3)}) scale(.85)`);
      drawings.forEach(({ node, length, scene, delay, end }) => {
        node.style.strokeDashoffset = String(length * (1 - growthFor(scene, delay, end)));
      });
      fills.forEach(({ node, baseOpacity, scene, delay, end }) => {
        node.style.fillOpacity = String(baseOpacity * growthFor(scene, delay, end));
      });
      growing.forEach(({ node, baseOpacity, reveal, bounds, hasOutline, scene, delay, end }) => {
        const growth = growthFor(scene, delay, end);
        if (reveal) {
          if (growth === 1) node.removeAttribute('clip-path');
          else node.setAttribute('clip-path', `url(#${reveal.parentElement.id})`);
          reveal.setAttribute('y', (bounds.y + bounds.height * (1 - growth)).toFixed(3));
          reveal.setAttribute('height', (bounds.height * growth).toFixed(3));
          node.style.opacity = String(growth > 0 ? baseOpacity : 0);
        } else node.style.opacity = String(hasOutline ? baseOpacity : baseOpacity * growth);
      });
      root.style.setProperty('--fs-progress', String(progress));
      progressBars.forEach(bar => {
        bar.style.transformOrigin = 'left center';
        bar.style.transform = `scaleX(${progress})`;
      });
      updateChapter(index);
    }

    function render() {
      frame = 0;
      if (destroyed || mode !== 'motion' || !canUseMotion) return;
      const { start, distance } = geometry();
      draw(clamp((window.scrollY - start) / distance));
    }

    function queueRender(force = false) {
      if (destroyed || mode !== 'motion' || (!nearby && !force) || frame) return;
      frame = window.requestAnimationFrame(render);
    }

    function updateViewBox() {
      if (art) art.setAttribute('viewBox', mobile.matches ? '40 110 1130 480' : originalViewBox);
    }

    function hasMotionRoom() {
      return window.innerHeight - topInset() >= 565;
    }

    function updateModeButtons() {
      const available = canUseMotion && hasMotionRoom();
      modeButtons.forEach(button => {
        const label = mode === 'motion' ? (root.dataset.fsReadLabel || 'Read the story') : (root.dataset.fsMotionLabel || 'Explore the animation');
        const labelNode = button.querySelector('[data-fs-mode-label]');
        if (labelNode) labelNode.textContent = label;
        else button.textContent = label;
        button.setAttribute('aria-label', label);
        button.setAttribute('aria-pressed', String(mode === 'reading'));
        button.hidden = !available;
        button.disabled = !available;
      });
    }

    function setMode(nextMode, reposition = false) {
      const next = nextMode === 'motion' && canUseMotion && hasMotionRoom() ? 'motion' : 'reading';
      if (next === mode) { updateModeButtons(); return; }
      const sectionTop = root.getBoundingClientRect().top + window.scrollY - topInset() - 16;
      window.cancelAnimationFrame(frame);
      frame = 0;
      mode = next;
      root.dataset.mode = mode;
      geometryCache = null;
      root.dataset.fsReducedMotion = String(reduced.matches);
      if (reading) {
        reading.hidden = false;
        reading.removeAttribute('aria-hidden');
      }
      updateModeButtons();
      updateChapter(0);
      if (mode === 'motion') {
        draw(0);
        if (reposition) scrollToTop(geometry().start);
        queueRender(true);
      } else {
        draw(1);
        if (reposition) scrollToTop(sectionTop);
      }
    }

    function goTo(index) {
      geometryCache = null;
      if (mode === 'reading') {
        const items = reading?.querySelectorAll('[data-fs-reading-chapter]');
        const destination = items?.[index] || reading?.children[index] || reading || root;
        scrollToTop(destination.getBoundingClientRect().top + window.scrollY - topInset() - 16, true);
        updateChapter(index);
        return;
      }
      const { start, distance } = geometry();
      const progress = index === sceneCount - 1 ? 1 : (index + 0.99) / sceneCount;
      scrollToTop(start + distance * progress, true);
      queueRender(true);
    }

    function readjustMode() {
      geometryCache = null;
      if (mode === 'motion' && !hasMotionRoom()) {
        const bounds = root.getBoundingClientRect();
        setMode('reading', bounds.top < window.innerHeight && bounds.bottom > 0);
      } else updateModeButtons();
      queueRender(true);
    }

    const onScroll = () => queueRender();
    const onResize = () => { updateViewBox(); readjustMode(); };
    const onReducedMotion = () => {
      geometryCache = null;
      root.dataset.fsReducedMotion = String(reduced.matches);
      if (reduced.matches) {
        const bounds = root.getBoundingClientRect();
        setMode('reading', bounds.top < window.innerHeight && bounds.bottom > 0);
      } else queueRender(true);
    };
    const onClick = event => {
      const modeButton = event.target.closest('[data-fs-mode]');
      if (modeButton && root.contains(modeButton)) {
        setMode(mode === 'motion' ? 'reading' : 'motion', true);
        return;
      }
      const button = event.target.closest('[data-fs-go]');
      if (button && root.contains(button)) goTo(navigationIndex(button));
      // Skip is an ordinary anchor: keep native navigation and focus behavior.
    };
    const onBlockSelect = event => {
      const chapter = event.target.closest?.('[data-fs-chapter]');
      if (chapter && root.contains(chapter)) goTo(sceneOf(chapter));
    };

    root.dataset.fsEnhanced = 'true';
    updateViewBox();
    setMode(reduced.matches || originalRootMode === 'reading' ? 'reading' : 'motion');
    root.addEventListener('click', onClick);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    window.addEventListener('load', onResize, { once: true });
    mobile.addEventListener('change', onResize);
    reduced.addEventListener('change', onReducedMotion);
    document.addEventListener('shopify:block:select', onBlockSelect);

    if ('IntersectionObserver' in window) {
      const intersection = new IntersectionObserver(entries => {
        nearby = entries.some(entry => entry.isIntersecting);
        if (nearby) queueRender(true);
        else { window.cancelAnimationFrame(frame); frame = 0; }
      }, { rootMargin: '500px 0px' });
      intersection.observe(root);
      cleanups.push(() => intersection.disconnect());
    }
    if ('ResizeObserver' in window && canUseMotion) {
      const resize = new ResizeObserver(readjustMode);
      resize.observe(rail);
      resize.observe(sticky);
      const header = document.querySelector('[data-ah-header]');
      if (header) resize.observe(header);
      if (document.body) resize.observe(document.body);
      cleanups.push(() => resize.disconnect());
    }
    document.fonts?.ready.then(() => { if (!destroyed) onResize(); });

    instances.set(root, () => {
      destroyed = true;
      window.cancelAnimationFrame(frame);
      root.removeEventListener('click', onClick);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('load', onResize);
      mobile.removeEventListener('change', onResize);
      reduced.removeEventListener('change', onReducedMotion);
      document.removeEventListener('shopify:block:select', onBlockSelect);
      cleanups.forEach(cleanup => cleanup());
      transforms.forEach((value, node) => {
        if (value === null) node.removeAttribute('transform');
        else node.setAttribute('transform', value);
      });
      if (art) art.setAttribute('viewBox', originalViewBox);
      chapters.forEach(chapter => { chapter.hidden = false; chapter.classList.remove('is-active'); });
      navigation.forEach(button => button.removeAttribute('aria-current'));
      originalModeButtons.forEach(({ button, hidden, disabled }) => { button.hidden = hidden; button.disabled = disabled; });
      progressBars.forEach(bar => { bar.style.removeProperty('transform'); bar.style.removeProperty('transform-origin'); });
      root.style.removeProperty('--fs-progress');
      root.removeAttribute('data-fs-enhanced');
      root.removeAttribute('data-fs-active');
      root.removeAttribute('data-fs-reduced-motion');
      if (originalRootMode === null) root.removeAttribute('data-mode');
      else root.dataset.mode = originalRootMode;
    });
  }

  function mountAll(scope = document) {
    if (scope.matches?.('[data-founder-story]')) mount(scope);
    scope.querySelectorAll('[data-founder-story]').forEach(mount);
  }

  window.AlawooderFounderStory = { mountAll };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mountAll(), { once: true });
  else mountAll();
  document.addEventListener('shopify:section:load', event => mountAll(event.target));
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((cleanup, root) => {
      if (event.target === root || event.target.contains(root)) {
        cleanup();
        instances.delete(root);
      }
    });
  });
})();
